import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';

const AI_CREDENTIAL_TYPES = new Set(['openai', 'gemini', 'google_generativeai']);

const ACTION_META = {
  add_node:    { label: 'Add Node',    color: '#22c55e' },
  modify_node: { label: 'Modify Node', color: '#f59e0b' },
  remove_node: { label: 'Remove Node', color: '#ef4444' },
};

const SUGGESTIONS = [
  'What does this workflow do?',
  'Add a step to send a Slack message',
  'Why did the last run fail?',
  'Add an HTTP request node',
];

export default function FlowCopilotPanel({
  workflowId,
  lastRunId,
  onClose,
  onApplyAddNode,
  onApplyModifyNode,
  onApplyRemoveNode,
}) {
  const [credentials, setCredentials] = useState([]);
  const [credentialId, setCredentialId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchCredentials();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/credentials/`, { headers });
      if (!res.ok) return;
      const all = await res.json();
      const ai = (Array.isArray(all) ? all : []).filter(c => AI_CREDENTIAL_TYPES.has(c.service_type));
      setCredentials(ai);
      if (ai.length > 0) setCredentialId(ai[0].id);
    } catch (_) {
      // silently ignore; error shown via credentials.length === 0
    }
  };

  const sendMessage = useCallback(async (text) => {
    const userMsg = (text ?? input).trim();
    if (!userMsg || !credentialId || loading) return;
    setInput('');
    setError('');

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    const history = newMessages.slice(0, -1).map(m => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(`${API_BASE_URL}/copilot/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          workflow_id: workflowId,
          credential_id: credentialId,
          message: userMsg,
          history,
          last_run_id: lastRunId || null,
        }),
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message || '',
        action_type: data.action_type || 'text',
        diff: data.diff || {},
      }]);
    } catch (e) {
      setError(e.message || 'Failed to get a response.');
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, credentialId, loading, messages, workflowId, lastRunId]);

  const handleApply = useCallback((msg, idx) => {
    if (msg.action_type === 'add_node' && msg.diff) {
      onApplyAddNode(msg.diff.type, msg.diff.label, msg.diff.params || {});
    } else if (msg.action_type === 'modify_node' && msg.diff) {
      onApplyModifyNode(msg.diff.node_label, msg.diff.params_patch || {});
    } else if (msg.action_type === 'remove_node' && msg.diff) {
      onApplyRemoveNode(msg.diff.node_label);
    }
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, applied: true } : m));
  }, [onApplyAddNode, onApplyModifyNode, onApplyRemoveNode]);

  const handleDismiss = (idx) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, dismissed: true } : m));
  };

  const isStructural = (msg) =>
    msg.role === 'assistant' && ['add_node', 'modify_node', 'remove_node'].includes(msg.action_type);

  const noCredentials = credentials.length === 0;

  return (
    <div style={{
      position: 'fixed',
      top: '44px',
      right: 0,
      bottom: 0,
      width: '360px',
      background: 'var(--bg-secondary)',
      borderLeft: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 900,
      boxShadow: '-4px 0 20px rgba(0,0,0,0.18)',
    }}>
      {/* Header */}
      <div style={{
        padding: '11px 14px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        background: 'var(--bg-secondary)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ fontSize: '15px' }}>✨</span>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>Flow Copilot</span>
          <span style={{ fontSize: '10px', padding: '1px 6px', background: 'var(--accent-color)', color: '#fff', borderRadius: '8px', fontWeight: 600 }}>AI</span>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 4px' }}
          title="Close"
        >×</button>
      </div>

      {/* Credential selector */}
      {noCredentials ? (
        <div style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--warning-color)', background: 'rgba(251,191,36,0.07)', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
          No AI credentials configured. Add an OpenAI or Gemini credential in Credential Manager to use the copilot.
        </div>
      ) : (
        <div style={{ padding: '7px 14px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
          <select
            value={credentialId}
            onChange={e => setCredentialId(e.target.value)}
            style={{
              width: '100%',
              padding: '4px 8px',
              background: 'var(--bg-input)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '5px',
              fontSize: '12px',
            }}
          >
            {credentials.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.service_type})</option>
            ))}
          </select>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 6px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {messages.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', textAlign: 'center', marginTop: '16px', lineHeight: 1.6 }}>
            <div style={{ fontSize: '26px', marginBottom: '8px' }}>✨</div>
            <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px', fontSize: '13px' }}>Ask anything about this workflow</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  disabled={noCredentials || loading}
                  style={{
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    color: 'var(--text-secondary)',
                    fontSize: '11px',
                    cursor: noCredentials ? 'default' : 'pointer',
                    textAlign: 'left',
                    opacity: noCredentials ? 0.5 : 1,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const structural = isStructural(msg);
          const meta = ACTION_META[msg.action_type];
          const settled = msg.applied || msg.dismissed;

          return (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
              {/* Bubble */}
              <div style={{
                maxWidth: '88%',
                padding: '7px 11px',
                borderRadius: isUser ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                background: isUser ? 'var(--accent-color)' : 'var(--bg-tertiary)',
                color: isUser ? '#fff' : 'var(--text-primary)',
                fontSize: '12px',
                lineHeight: 1.55,
                wordBreak: 'break-word',
              }}>
                {msg.content}
              </div>

              {/* Suggestion card */}
              {structural && !settled && (
                <div style={{
                  marginTop: '5px',
                  width: '100%',
                  background: 'var(--bg-primary)',
                  border: `1px solid ${meta?.color || 'var(--accent-color)'}`,
                  borderRadius: '8px',
                  padding: '9px 11px',
                  fontSize: '12px',
                }}>
                  {/* Action badge + node label */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '1px 8px',
                      background: meta?.color || 'var(--accent-color)',
                      color: '#fff',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {meta?.label || msg.action_type}
                    </span>
                    {msg.diff?.label && (
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '12px' }}>{msg.diff.label}</span>
                    )}
                    {msg.diff?.node_label && (
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '12px' }}>{msg.diff.node_label}</span>
                    )}
                    {msg.diff?.type && (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>({msg.diff.type})</span>
                    )}
                  </div>

                  {/* Params preview */}
                  {msg.diff?.params && Object.keys(msg.diff.params).length > 0 && (
                    <pre style={{
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-tertiary)',
                      padding: '5px 8px',
                      borderRadius: '4px',
                      maxHeight: '80px',
                      overflow: 'auto',
                      margin: '0 0 7px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}>
                      {JSON.stringify(msg.diff.params, null, 2)}
                    </pre>
                  )}
                  {msg.diff?.params_patch && Object.keys(msg.diff.params_patch).length > 0 && (
                    <pre style={{
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-tertiary)',
                      padding: '5px 8px',
                      borderRadius: '4px',
                      maxHeight: '80px',
                      overflow: 'auto',
                      margin: '0 0 7px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}>
                      {JSON.stringify(msg.diff.params_patch, null, 2)}
                    </pre>
                  )}

                  {/* Apply / Dismiss */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleApply(msg, idx)}
                      style={{
                        padding: '4px 14px',
                        background: 'var(--accent-color)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      Apply
                    </button>
                    <button
                      onClick={() => handleDismiss(idx)}
                      style={{
                        padding: '4px 14px',
                        background: 'transparent',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '11px',
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Applied / dismissed badge */}
              {structural && settled && (
                <div style={{ marginTop: '3px', fontSize: '10px', color: 'var(--text-tertiary)' }}>
                  {msg.applied ? '✓ Applied to canvas' : '— Dismissed'}
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{
              padding: '7px 11px',
              background: 'var(--bg-tertiary)',
              borderRadius: '12px 12px 12px 3px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
            }}>
              Thinking…
            </div>
          </div>
        )}

        {error && (
          <div style={{
            fontSize: '11px',
            color: 'var(--error-color)',
            padding: '6px 10px',
            background: 'rgba(239,68,68,0.08)',
            borderRadius: '6px',
          }}>
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={{
        padding: '9px 10px',
        borderTop: '1px solid var(--border-color)',
        flexShrink: 0,
        display: 'flex',
        gap: '6px',
        alignItems: 'flex-end',
        background: 'var(--bg-secondary)',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder={noCredentials ? 'Add an AI credential to start' : 'Ask anything… (Enter to send, Shift+Enter for newline)'}
          disabled={noCredentials || loading}
          rows={2}
          style={{
            flex: 1,
            resize: 'none',
            background: 'var(--bg-input)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '7px 10px',
            fontSize: '12px',
            fontFamily: 'inherit',
            lineHeight: 1.45,
            outline: 'none',
            opacity: noCredentials ? 0.5 : 1,
          }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || noCredentials || loading}
          title="Send (Enter)"
          style={{
            padding: '7px 11px',
            background: input.trim() && !noCredentials && !loading ? 'var(--accent-color)' : 'var(--bg-tertiary)',
            color: input.trim() && !noCredentials && !loading ? '#fff' : 'var(--text-tertiary)',
            border: 'none',
            borderRadius: '8px',
            cursor: input.trim() && !noCredentials && !loading ? 'pointer' : 'default',
            fontSize: '16px',
            lineHeight: 1,
            flexShrink: 0,
            alignSelf: 'flex-end',
            transition: 'background 0.15s',
          }}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
